import { getHybridApp } from '@vmos2/core';
import noop from 'lodash/noop';
import { useEffect, useState } from 'react';

export const useHybridApp = () => {
  const [hybridApp, setHybridApp] = useState<{
    enablePushNotifications?: () => Promise<void>,
    hideSplashScreen?: () => void,
    getPushNotificationsDetails: () => Promise<void | {
      pushNotificationEnabled: boolean,
      pushNotificationToken: string,
    }>,
  }>();
  const [isLoading, setIsLoading] = useState(true);
  const abortController = new AbortController();

  useEffect(() => {
    getHybridApp().then(data => {
      if (!abortController.signal.aborted) {
        setHybridApp(data);
        setIsLoading(false);
      }
    });

    return () => {
      abortController.abort('Component was unmounted');
    };
  }, []);

  const copyToClipboard = (text: string, onSuccess = noop, onError = noop) => {
    if (!hybridApp) {
      return;
    }

    if (!window.cordova && !window.ReactNative) {
      return;
    }

    if (window.cordova) {
      window.cordova.plugins?.clipboard?.copy(text, onSuccess, onError);

      return;
    }

    if (window.ReactNative) {
      window.ReactNative.copyToClipboard?.(text, onSuccess, onError);
    }
  };

  const setStatusBarStyleFromTheme = (
    theme: { color?: Record<string, string> } | null,
    colorName: string | null,
    overrideColor: string | null
  ) => {
    const color = overrideColor || theme?.color?.[colorName || 'white'];

    if (color) {
      window?.ReactNative?.setStatusBarStyleByColor?.(color);
    }
  };

  const setStatusBarTransparent = (transparent: boolean) => window?.ReactNative?.setStatusBarTransparent?.(transparent);

  const enablePushNotifications = async (mobilePushNotificationsEnabled: boolean) => {
    if (hybridApp && hybridApp.enablePushNotifications && mobilePushNotificationsEnabled) {
      const result = await hybridApp.enablePushNotifications();

      return result;
    }

    return null;
  };

  const setStatusBarHidden = (hidden: boolean) => window?.ReactNative?.setStatusBarHidden(hidden);

  const getUserDevicePushNotificationsDetails = async () => {
    if (!hybridApp || !hybridApp.getPushNotificationsDetails) {
      return undefined;
    }

    const pushNotificationsDetails = await hybridApp.getPushNotificationsDetails();

    return pushNotificationsDetails;
  };

  return {
    copyToClipboard,
    enablePushNotifications,
    getUserDevicePushNotificationsDetails,
    hybridApp,
    isLoading,
    setStatusBarHidden,
    setStatusBarStyleFromTheme,
    setStatusBarTransparent,
  };
};
